import { type FormEvent, useCallback, useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { apiFetch } from "@common/lib/api";
import type { ClinicResult } from "../setup-flow-config";

interface AddressSuggestion {
    formattedAddress: string;
    street: string;
    city: string;
    state: string;
    zip: string;
    country: string;
    phone: string | null;
}

interface Props {
    clinicName: string;
    onSubmit: (result: ClinicResult) => Promise<void> | void;
}

export default function ClinicManualStep({ clinicName, onSubmit }: Props) {
    const [name, setName] = useState(clinicName);
    const [street, setStreet] = useState("");
    const [city, setCity] = useState("");
    const [state, setState] = useState("");
    const [zip, setZip] = useState("");
    const [phone, setPhone] = useState("");
    const [country, setCountry] = useState("US");

    // Address autocomplete
    const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
    const wrapperRef = useRef<HTMLDivElement>(null);

    const fetchSuggestions = useCallback(async (query: string) => {
        if (query.length < 3) {
            setSuggestions([]);
            return;
        }
        try {
            const results = await apiFetch<AddressSuggestion[]>("onboarding/address-autocomplete", {
                method: "POST",
                json: { query },
            });
            setSuggestions(results || []);
            setShowSuggestions(true);
        } catch (error) {
            Sentry.captureException(error);
            setSuggestions([]);
        }
    }, []);

    const handleStreetChange = (value: string) => {
        setStreet(value);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => fetchSuggestions(value), 300);
    };

    const selectSuggestion = (suggestion: AddressSuggestion) => {
        setStreet(suggestion.street);
        setCity(suggestion.city);
        setState(suggestion.state);
        setZip(suggestion.zip);
        if (suggestion.country) setCountry(suggestion.country);
        if (suggestion.phone) setPhone(suggestion.phone);
        setSuggestions([]);
        setShowSuggestions(false);
    };

    // Clean up debounce timer on unmount
    useEffect(() => {
        return () => clearTimeout(debounceRef.current);
    }, []);

    // Close suggestions on click outside
    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, []);

    const isValid = name.trim() && street.trim() && city.trim() && state.trim();
    const savingRef = useRef(false);

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        if (!isValid || savingRef.current) return;
        savingRef.current = true;
        try {
            await onSubmit({
                name: name.trim() || clinicName,
                formattedAddress: [street, city, state, zip].filter(Boolean).join(", "),
                street,
                city,
                state,
                zip,
                country,
                phone: phone || null,
                website: null,
            });
        } finally {
            savingRef.current = false;
        }
    };

    const inputClass =
        "w-full rounded-xl border border-shadow-100 bg-white px-4 py-3.5 text-lg text-shadow-700 placeholder:text-shadow-400 focused-inset";

    return (
        <form
            onSubmit={handleSubmit}
            className="flex w-full max-w-[520px] flex-col items-center gap-7"
            autoComplete="off"
        >
            <h1 className="fraunces-soft text-4xl font-semibold leading-tight text-shadow-700">Clinic details</h1>
            <p className="text-lg text-shadow-500">Just the basics — you can update these later in Settings</p>

            {/* Clinic name (editable) */}
            <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Clinic name"
                autoComplete="off"
                // eslint-disable-next-line jsx-a11y/no-autofocus -- sole input of this onboarding wizard step; autofocus is intentional UX
                autoFocus
                className={inputClass}
            />

            {/* Street address with autocomplete */}
            <div ref={wrapperRef} className="relative z-50 w-full">
                <input
                    type="text"
                    value={street}
                    onChange={(e) => handleStreetChange(e.target.value)}
                    onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                    placeholder="Street address"
                    autoComplete="off"
                    className={inputClass}
                />
                {showSuggestions && suggestions.length > 0 && (
                    <div className="absolute left-0 right-0 top-full z-50 mt-1 overflow-hidden rounded-xl border border-shadow-100 bg-white shadow-lg">
                        {suggestions.map((s, i) => (
                            <button
                                key={i}
                                type="button"
                                onClick={() => selectSuggestion(s)}
                                className="w-full px-4 py-3 text-left text-sm text-shadow-600 hover:bg-max-50 transition-colors"
                            >
                                {s.formattedAddress}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* City / State / ZIP row */}
            <div className="flex w-full gap-3">
                <input
                    type="text"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="City"
                    autoComplete="off"
                    className={`${inputClass} min-w-0 flex-[2]`}
                />
                <input
                    type="text"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    placeholder="State"
                    autoComplete="off"
                    className={`${inputClass} min-w-0 flex-1`}
                />
                <input
                    type="text"
                    value={zip}
                    onChange={(e) => setZip(e.target.value)}
                    placeholder="ZIP"
                    autoComplete="off"
                    className={`${inputClass} min-w-0 flex-1`}
                />
            </div>

            {/* Phone */}
            <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Phone number"
                autoComplete="off"
                className={inputClass}
            />

            {/* Continue button */}
            <button
                type="submit"
                disabled={!isValid}
                className="w-full rounded-xl bg-max-900 py-3.5 text-lg font-semibold text-white hover:bg-max-850 disabled:bg-gray-100 disabled:text-gray-400 focused"
            >
                Continue
            </button>
        </form>
    );
}
